// src/utils/forms/validationHelper.js

/**
 * ✅ Compare two times (HH:mm format)
 * 
 * @param {String} time1 - First time
 * @param {String} time2 - Second time
 * @returns {Boolean} - true if time2 is after time1
 */
export const isTimeAfter = (time1, time2) => {
    if (!time1 || !time2) return true; // Skip if either is empty

    const [h1, m1] = time1.split(':').map(Number);
    const [h2, m2] = time2.split(':').map(Number);

    const minutes1 = h1 * 60 + m1;
    const minutes2 = h2 * 60 + m2;

    return minutes1 < minutes2;
};

/**
 * ✅ Format time to HH:mm (without seconds)
 * 
 * @param {String} timeString - Time string
 * @returns {String|null} - Formatted time or null
 */
export const formatTime = (timeString) => {
    if (!timeString || timeString.trim() === '') return null;

    // Already in HH:mm format
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (timeRegex.test(timeString)) {
        return timeString;
    }

    // Has seconds, remove them
    const timeRegexWithSeconds = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/;
    if (timeRegexWithSeconds.test(timeString)) {
        return timeString.substring(0, 5);
    }

    return null;
};

/**
 * ✅ Format date to YYYY-MM-DD
 * 
 * @param {String} dateString - Date string
 * @returns {String|null} - Formatted date or null
 */
export const formatDate = (dateString) => {
    if (!dateString || dateString.trim() === '') return null;

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return null;

    return dateString;
};

/**
 * ✅ Validate file type for images
 * 
 * @param {File} file - File object
 * @param {Array} validTypes - Valid MIME types
 * @returns {Boolean}
 */
export const isValidImageType = (file, validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']) => {
    return validTypes.includes(file.type);
};

/**
 * ✅ Validate file size
 * 
 * @param {File} file - File object
 * @param {Number} maxSizeMB - Maximum size in MB
 * @returns {Boolean}
 */
export const isValidFileSize = (file, maxSizeMB = 5) => {
    const maxSize = maxSizeMB * 1024 * 1024;
    return file.size <= maxSize;
};

/**
 * ✅ Check if end date is after start date
 * 
 * @param {String} startDate - Start date (YYYY-MM-DD)
 * @param {String} endDate - End date (YYYY-MM-DD)
 * @returns {Boolean}
 */
export const isEndDateAfterStartDate = (startDate, endDate) => {
    if (!startDate || !endDate) return true;

    const start = new Date(startDate);
    const end = new Date(endDate);

    return end > start;
};
